<!-- The review body (#reviewBody) shows on the bid's page, or in a dialog when opened from the PO -->
@if (inDialog) {
  <div class="vqr vqr--dlg" role="dialog" aria-label="Review extracted quote">
    <ng-container *ngTemplateOutlet="reviewBody"></ng-container>
  </div>
} @else {
<app-layout>
  <main class="admin-main admin-content vqr">
    <div class="container-fluid">
      <!-- D1: breadcrumb (same markup as the Bid detail page) -->
      <nav class="vqr-breadcrumb" aria-label="Breadcrumb navigation">
        <ol class="breadcrumb-list">
          <li class="breadcrumb-item">
            <a routerLink="/bids" class="breadcrumb-link"><mat-icon class="breadcrumb-icon">gavel</mat-icon><span>Bids</span></a>
          </li>
          <li class="breadcrumb-separator"><mat-icon>chevron_right</mat-icon></li>
          <li class="breadcrumb-item">
            <a [routerLink]="['/bids/bid', bidId]" class="breadcrumb-link"><span>#{{ bidLabel }}</span></a>
          </li>
          <li class="breadcrumb-separator"><mat-icon>chevron_right</mat-icon></li>
          <li class="breadcrumb-item">
            <a class="breadcrumb-link" (click)="backToVendorQuotes()" href="javascript:void(0)"><span>Vendor Quotes</span></a>
          </li>
          @if (draft) {
            <li class="breadcrumb-separator"><mat-icon>chevron_right</mat-icon></li>
            <li class="breadcrumb-item active" aria-current="page">
              <span class="breadcrumb-current">Review {{ draft.quoteNumber.value }}</span>
            </li>
          }
        </ol>
      </nav>

      <ng-container *ngTemplateOutlet="reviewBody"></ng-container>
    </div>
  </main>
</app-layout>
}

<ng-template #reviewBody>
      @if (isLoading) {
        <div class="vqr-box vqr-state"><mat-spinner diameter="40"></mat-spinner></div>
      } @else if (!draft) {
        <div class="vqr-box vqr-state">
          <h2>No uploaded quote to review</h2>
          <p class="muted">This draft is no longer available. Upload the vendor quote again from the Vendor Quotes tab.</p>
          <button class="vqr-btn vqr-btn-go" type="button" (click)="backToVendorQuotes()">Go to Vendor Quotes</button>
        </div>
      } @else {
        <!-- D1 + D2: header -->
        <section class="vqr-hd">
          <div class="r">
            <h1>Review Extracted Quote <span class="bar">|</span> <span class="nm">{{ draft.quoteNumber.value }}</span></h1>
            <span class="vqr-tag draft">Draft</span>
            <span class="sp"></span>
            <button class="vqr-btn" type="button" (click)="cancelDraft()"
              [matTooltip]="inDialog ? 'Close without using this quote.' : 'Discards this draft. You are asked to confirm first.'">Cancel</button>
            <button class="vqr-btn" type="button" (click)="toggleEdit()"
              matTooltip="Change any value we read from the document.">
              <mat-icon>edit</mat-icon>{{ editing ? 'Done Editing' : 'Edit' }}
            </button>
            @if (inDialog) {
              <button class="vqr-btn vqr-btn-go" type="button" (click)="saveQuote()"
                matTooltip="Shows what this quote changes on the PO before anything is applied.">
                Review Changes<mat-icon>arrow_forward</mat-icon>
              </button>
            } @else {
              <button class="vqr-btn vqr-btn-go" type="button" (click)="saveQuote()" [disabled]="isSaving"
                [matTooltip]="draft.scopes.length ? 'Saves the quote and its materials to ' + bidLabel + '.' : 'Choose a scope in the Quote section first.'">
                <mat-icon>check</mat-icon>Save Quote
              </button>
            }
          </div>
          <div class="meta">
            @if (inDialog) {
              <span>For <b>{{ dialogData?.forLabel }}</b></span>
            } @else {
              <span>Added to <b>{{ bidLabel }}@if (bid?.name) {<span> · {{ bid?.name }}</span>}</b></span>
            }
            <span>Source <b>{{ draft.file.name }}</b></span>
          </div>
        </section>

        <!-- D3: notice -->
        <div class="vqr-tip">
          <mat-icon>auto_awesome</mat-icon>
          <span class="t">
            <b>We read this information from {{ draft.file.name }}.</b>
            <span class="srcb"><mat-icon>auto_awesome</mat-icon></span> = read from document,
            <span class="srcb man">✎</span> = manually entered.
            @if (flagCount) {
              <b>{{ flagCount }} value{{ flagCount === 1 ? ' needs' : 's need' }} a quick check</b> (highlighted in yellow), because that part of the document was hard to read.
            } @else {
              Everything was read clearly.
            }
            @if (newCount) {
              <b>{{ newCount }} item{{ newCount === 1 ? ' is' : 's are' }} not in your catalog</b> (marked blue) and will be added as a new product when you save.
            }
            @if (sameNameCount) {
              <b>{{ sameNameCount }} new products share a name</b> with another line. Click <b>Edit</b> and rename them (for example add "3 Wide") before saving.
            }
            @if (inDialog) {
              Then <b>Review Changes</b> to see what it does to this PO.
            } @else {
              Choose the <b>scope</b> in the Quote section, then <b>Save Quote</b>.
            }
          </span>
        </div>

        <div class="vqr-rv">
          <!-- D4: document preview -->
          <div class="vqr-doc">
            <div class="bar"><mat-icon>description</mat-icon>{{ draft.file.name }}<span class="sp"></span><span class="muted small">{{ draft.file.size }}</span></div>
            @if (previewUrl) {
              <iframe class="paper" [src]="previewUrl" [title]="'Preview of ' + draft.file.name"></iframe>
            } @else if (imageUrl) {
              <img class="paper img" [src]="imageUrl" [alt]="'Preview of ' + draft.file.name">
            } @else {
              <div class="paper empty"><mat-icon>description</mat-icon><span>Preview not available for this file.</span></div>
            }
          </div>

          <div class="vqr-side">
            <!-- D5: vendor -->
            <section class="vqr-box">
              <h2>Vendor <span class="sp"></span>
                @if (draft.vendorIsNew) {
                  <span class="vqr-tag new">Added just now</span>
                } @else {
                  <span class="vqr-tag use">Existing vendor</span>
                }
              </h2>
              <div class="fields">
                <ng-container *ngTemplateOutlet="readOnlyField; context: { label: 'Vendor name', field: draft.vendor.name }"></ng-container>
                <ng-container *ngTemplateOutlet="readOnlyField; context: { label: 'Contact', field: draft.vendor.contact }"></ng-container>
                <ng-container *ngTemplateOutlet="readOnlyField; context: { label: 'Email', field: draft.vendor.email }"></ng-container>
                <ng-container *ngTemplateOutlet="readOnlyField; context: { label: 'Phone', field: draft.vendor.phone }"></ng-container>
              </div>
            </section>

            <!-- D6: quote -->
            <section class="vqr-box">
              <h2>Quote</h2>
              <div class="fields">
                <div class="f">
                  <label for="d-code">Quote #<ng-container *ngTemplateOutlet="srcBadge; context: { field: draft.quoteNumber, label: 'Quote #' }"></ng-container></label>
                  @if (editing) {
                    <input id="d-code" type="text" [(ngModel)]="draft.quoteNumber.value" (ngModelChange)="onFieldChange(draft.quoteNumber)">
                  } @else {
                    <span class="v">{{ draft.quoteNumber.value }}</span>
                  }
                </div>
                <div class="f">
                  <label for="d-date">Quote date<ng-container *ngTemplateOutlet="srcBadge; context: { field: draft.quoteDate, label: 'Quote date' }"></ng-container></label>
                  @if (editing) {
                    <input id="d-date" type="date" [(ngModel)]="draft.quoteDate.value" (ngModelChange)="onFieldChange(draft.quoteDate)">
                  } @else {
                    <span class="v">{{ formatDate(draft.quoteDate.value) }}</span>
                  }
                </div>
                <div class="f">
                  <label for="d-valid">Valid till<ng-container *ngTemplateOutlet="srcBadge; context: { field: draft.validTill, label: 'Valid till' }"></ng-container></label>
                  @if (editing) {
                    <input id="d-valid" type="date" [(ngModel)]="draft.validTill.value" (ngModelChange)="onFieldChange(draft.validTill)">
                  } @else {
                    <span class="v">{{ formatDate(draft.validTill.value) }}</span>
                  }
                </div>
                @if (!inDialog) {
                <div class="f full" id="rv-scopes">
                  <label for="d-scopes">Scopes <span class="req">*</span></label>
                  <ng-select labelForId="d-scopes" class="vqr-scopes" [class.need]="!draft.scopes.length && !scopeError" [class.err]="scopeError && !draft.scopes.length"
                    [items]="scopeOptions" bindLabel="label" bindValue="id" [multiple]="true" [searchable]="false"
                    [clearable]="false" placeholder="Select Scopes" [(ngModel)]="draft.scopes" (change)="onScopesChange()">
                    <ng-template ng-option-tmp let-item="item">
                      <span class="opt">{{ item.label }}@if (item.suggested) {<span class="sg">Matches {{ item.matchCount }} of {{ matchedLineCount }} matched item{{ matchedLineCount === 1 ? '' : 's' }}</span>}</span>
                    </ng-template>
                  </ng-select>
                  @if (autoScopesKept.length) {
                    <span class="auto-scope">
                      <span class="srcb calc sm" matTooltip="Selected by us from the scopes of the items already in your catalog, not read from the document."><mat-icon>calculate</mat-icon></span>
                      <span>Selected from your matched items:
                        @for (s of autoScopesKept; track s.id; let last = $last) {
                          <b>{{ s.label }}</b> matches {{ s.matchCount }} of {{ matchedLineCount }}{{ last ? '' : ';' }}
                        }
                        @if (newCount) { ({{ newCount }} new product{{ newCount === 1 ? '' : 's' }} not counted: they get the scopes you save with.) }
                        Check before saving.</span>
                    </span>
                  }
                  @if (offBidScopes.length) {
                    <span class="help">
                      Matched items are also in
                      @for (s of offBidScopes; track s.label; let last = $last) {<b>{{ s.label }}</b> ({{ s.count }} item{{ s.count === 1 ? '' : 's' }}){{ last ? ', which ' + (offBidScopes.length === 1 ? 'is' : 'are') + ' not on ' + bidLabel + ', so ' + (offBidScopes.length === 1 ? 'it' : 'they') + ' can’t be chosen here.' : ', ' }}}
                    </span>
                  }
                  @if (draft.scopes.length) {
                    <span class="help">This quote will be saved for {{ draft.scopes.length === 1 ? 'this scope' : 'these scopes' }} of {{ bidLabel }}.</span>
                  } @else if (scopeError) {
                    <span class="err-text">Choose at least one scope before saving.</span>
                  } @else {
                    <span class="need-text"><mat-icon>info</mat-icon>Choose which scope(s) of {{ bidLabel }} this quote is for. Required to save.</span>
                  }
                </div>
                }
              </div>
            </section>

            <!-- D7 + D8: materials and totals -->
            <section class="vqr-box">
              <h2>Materials <span class="muted small cnt">{{ draft.lines.length }} found in the document</span><span class="sp"></span>
                <span class="mcnt ok"><mat-icon>link</mat-icon>{{ matchedCount }} matched</span>
                @if (newCount) {
                  <span class="mcnt nw"><mat-icon>add</mat-icon>{{ newCount }} new product{{ newCount === 1 ? '' : 's' }}</span>
                }
                @if (creditCount) {
                  <span class="mcnt cr" matTooltip="Lines with a negative quantity or price — credits, returns or discounts. They are not saved with the quote."><mat-icon>remove_circle_outline</mat-icon>{{ creditCount }} credit{{ creditCount === 1 ? '' : 's' }} left out</span>
                }
              </h2>
              <div class="src-legend" aria-label="What the badges mean">
                <span><span class="srcb sm"><mat-icon>auto_awesome</mat-icon></span>From document</span>
                <span><span class="srcb calc sm"><mat-icon>calculate</mat-icon></span>Calculated by us, not printed on the document</span>
                <span><span class="srcb man sm">✎</span>Entered by you</span>
              </div>
              <div class="tw">
                <table class="mt">
                  <thead>
                    <tr><th>#</th><th>Item in document</th><th>Your product</th><th class="r">Qty</th><th>Unit</th><th class="r">Unit price</th><th class="r">Total</th></tr>
                  </thead>
                  <tbody>
                    @for (line of draft.lines; track trackByLine($index, line); let k = $index) {
                      <tr [id]="'rv-line-' + k" [class.flag]="line.needsCheck && !line.rejected" [class.newp]="!line.matchedProduct && !line.rejected"
                        [class.dup]="sameNameAs(line).length" [class.rej]="line.rejected">
                        <td>{{ k + 1 }}</td>
                        <td>
                          @if (editing && !line.matchedProduct && !line.rejected) {
                            <input class="nmi" type="text" maxlength="255" [value]="line.name" (change)="onNameChange(line, $any($event.target).value)"
                              [attr.aria-label]="'Product name for line ' + (k + 1)" matTooltip="The name this new product gets in your catalog">
                          } @else {
                            <b class="nm">{{ line.name }}</b>
                          }
                          @if (line.description && line.description !== line.name) {
                            <div class="small muted">{{ line.description }}</div>
                          }
                          @if (sameNameAs(line); as others) {
                            @if (others.length) {
                              <div class="dupn"><mat-icon>content_copy</mat-icon>Same name as line {{ others.join(', ') }}. Rename one so you can tell them apart.</div>
                            }
                          }
                        </td>
                        <td>
                          @if (line.rejected) {
                            <span class="crb" tabindex="0" matTooltip="A credit, return or discount on the document (negative quantity or price). It is shown here but not saved with the quote, and creates no product.">
                              <mat-icon>remove_circle_outline</mat-icon>Credit · left out
                            </span>
                          } @else if (line.matchedProduct) {
                            <a class="match plink" [routerLink]="['/products/product', line.matchedProduct.id]" target="_blank" rel="noopener"
                              matTooltip="Matched to this product in your catalog. Opens its details in a new tab."><mat-icon>link</mat-icon>{{ line.matchedProduct.title }}</a>
                          } @else {
                            <span class="npb" tabindex="0" (mouseenter)="showNewProductTip(line, $event)" (mouseleave)="hideNewProductTip()"
                              (focus)="showNewProductTip(line, $event)" (blur)="hideNewProductTip()">
                              <mat-icon>add</mat-icon>New product<span class="i">i</span>
                            </span>
                            <div class="help">Not found in your catalog</div>
                          }
                        </td>
                        <td class="r num">
                          @if (editing && !line.rejected) {
                            <input type="number" min="0" step="1" [ngModel]="line.qty.value" (ngModelChange)="onQtyChange(line, $event)" [attr.aria-label]="'Quantity for ' + line.name">
                          } @else {
                            {{ line.qty.value }}
                          }
                          @if (line.qty.source === 'manual') {
                            <span class="srcb man sm" tabindex="0" matTooltip="Manually entered">✎</span>
                          } @else if (line.qty.source === 'calculated') {
                            <span class="srcb calc sm" tabindex="0" [matTooltip]="calcTooltip(line.qty)" aria-label="Quantity calculated, not from the document"><mat-icon>calculate</mat-icon></span>
                          } @else if (line.qty.value != null) {
                            <span class="srcb sm" tabindex="0" matTooltip="Read from document" aria-label="Quantity read from the document"><mat-icon>auto_awesome</mat-icon></span>
                          }
                        </td>
                        <td class="num">{{ line.unit }}
                          @if (line.unit) {
                            <span class="srcb sm" tabindex="0" matTooltip="Read from document" aria-label="Unit read from the document"><mat-icon>auto_awesome</mat-icon></span>
                          }
                        </td>
                        <td class="r num">
                          @if (editing && !line.rejected) {
                            <input type="number" min="0" step="0.01" [value]="line.unitPrice.value != null ? line.unitPrice.value.toFixed(2) : ''" (change)="onPriceChange(line, $any($event.target).value)" [attr.aria-label]="'Unit price for ' + line.name">
                          } @else {
                            {{ line.unitPrice.value | currency }}
                          }
                          @if (line.unitPrice.source === 'manual') {
                            <span class="srcb man sm" tabindex="0" matTooltip="Manually entered">✎</span>
                          } @else if (line.unitPrice.source === 'calculated') {
                            <span class="srcb calc sm" tabindex="0" [matTooltip]="calcTooltip(line.unitPrice)" aria-label="Unit price calculated, not from the document"><mat-icon>calculate</mat-icon></span>
                          } @else if (line.unitPrice.value != null) {
                            <span class="srcb sm" tabindex="0" matTooltip="Read from document" aria-label="Unit price read from the document"><mat-icon>auto_awesome</mat-icon></span>
                          }
                          @if (line.needsCheck && !line.rejected) {
                            <div><span class="chk"><mat-icon>error_outline</mat-icon>Check this</span></div>
                          }
                        </td>
                        <td class="r num">
                          @if (line.useDocTotal && line.lineTotal != null) {
                            <!-- Using the document's line total: it goes on top with ✦, ours below -->
                            <b>{{ line.lineTotal | currency }}</b>
                            <span class="srcb sm" tabindex="0" matTooltip="The line total printed on the document, used for this line. The unit price is not changed."
                              aria-label="Line total from the document"><mat-icon>auto_awesome</mat-icon></span>
                            <div class="doct">
                              <mat-icon class="calc">calculate</mat-icon>Calc {{ calcLineTotal(line) | currency }}
                              <button type="button" class="ltsw" (click)="toggleDocTotal(line)" matTooltip="Use our calculated total (quantity × unit price) instead"
                                [attr.aria-label]="'Use the calculated total for line ' + (k + 1)"><mat-icon>swap_vert</mat-icon></button>
                            </div>
                          } @else {
                            <b>{{ calcLineTotal(line) | currency }}</b>
                            <span class="srcb calc sm" tabindex="0" matTooltip="Calculated by us: quantity × unit price."
                              aria-label="Line total calculated: quantity times unit price"><mat-icon>calculate</mat-icon></span>
                            @if (line.lineTotal != null) {
                              <div class="doct" [class.diff]="lineDiffers(line)"
                                [matTooltip]="lineDiffers(line) ? 'The document prints a different total for this line' : 'Line total printed on the document'">
                                <mat-icon>auto_awesome</mat-icon>Doc {{ line.lineTotal | currency }}
                                @if (lineDiffers(line)) {
                                  <button type="button" class="ltsw" (click)="toggleDocTotal(line)" matTooltip="Use the document's total for this line"
                                    [attr.aria-label]="'Use the document total for line ' + (k + 1)"><mat-icon>swap_vert</mat-icon></button>
                                }
                              </div>
                            }
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
              <div class="totals">
                <div><span>Materials<span class="srcb calc sm" tabindex="0" matTooltip="Calculated by us: the sum of quantity × unit price on every line."><mat-icon>calculate</mat-icon></span></span><b class="num">{{ materialsTotal | currency }}</b></div>
                @if (draft.freight) {
                  <div><span>Freight
                    @if (draft.freightBasis) {
                      <span class="srcb calc sm" tabindex="0" [matTooltip]="'Calculated by us. ' + draft.freightBasis" aria-label="Freight calculated, not from the document"><mat-icon>calculate</mat-icon></span>
                    } @else {
                      <span class="srcb sm" tabindex="0" matTooltip="Read from document"><mat-icon>auto_awesome</mat-icon></span>
                    }
                  </span><b class="num">{{ draft.freight | currency }}</b></div>
                }
                <div class="g qt">
                  <span>Quote total
                    @switch (totalSource) {
                      @case ('document') { <span class="srcb sm" tabindex="0" matTooltip="The total printed on the document"><mat-icon>auto_awesome</mat-icon></span> }
                      @case ('manual') { <span class="srcb man sm" tabindex="0" matTooltip="Entered by you">✎</span> }
                      @default { <span class="srcb calc sm" tabindex="0" matTooltip="Calculated by us: materials + freight"><mat-icon>calculate</mat-icon></span> }
                    }
                  </span>
                  @if (totalSource === 'manual') {
                    <input class="qti num" type="number" min="0" step="0.01" [value]="(draft.quoteTotal ?? 0).toFixed(2)"
                      (change)="onQuoteTotalInput($any($event.target).value)" aria-label="Quote total">
                  } @else {
                    <span class="num">{{ quoteTotal | currency }}</span>
                  }
                  <!-- Calculated / Document / Custom — one ⋮ menu keeps the row compact -->
                  <button type="button" class="qtm" [matMenuTriggerFor]="qtMenu" matTooltip="Choose the quote total" aria-label="Choose the quote total">
                    <mat-icon>more_vert</mat-icon>
                  </button>
                  <mat-menu #qtMenu="matMenu" xPosition="before">
                    <button mat-menu-item (click)="setTotalSource('calculated')" matTooltip="Materials + freight" matTooltipPosition="left">
                      <mat-icon>calculate</mat-icon><span>Calculated Total</span>
                    </button>
                    <button mat-menu-item (click)="setTotalSource('document')" [disabled]="documentQuoteTotal == null">
                      <mat-icon>auto_awesome</mat-icon><span>{{ documentQuoteTotal == null ? 'Document Total (not on document)' : 'Document Total' }}</span>
                    </button>
                    <button mat-menu-item (click)="setTotalSource('manual')">
                      <mat-icon>edit</mat-icon><span>Custom Total</span>
                    </button>
                  </mat-menu>
                </div>
                @if (documentLinesTotal != null) {
                  <div class="doc"><span>Document line totals<span class="srcb sm" tabindex="0" matTooltip="The line totals printed on the document, added up"><mat-icon>auto_awesome</mat-icon></span></span><b class="num">{{ documentLinesTotal | currency }}</b></div>
                }
                @if (documentQuoteTotal != null) {
                  <div class="doc g"><span>Document quote total<span class="srcb sm" tabindex="0" matTooltip="Read from document"><mat-icon>auto_awesome</mat-icon></span></span><span class="num">{{ documentQuoteTotal | currency }}</span></div>
                } @else {
                  <div class="doc"><span>Document quote total</span><span class="nod">Not on document</span></div>
                }
                @if (totalSource !== 'calculated') {
                  <!-- Document / Custom fix the total; proposals still price from the lines, so say if they disagree -->
                  <span class="match" [class.no]="linesGap <= -0.005 || linesGap >= 0.005">
                    @if (linesGap > -0.005 && linesGap < 0.005) {
                      <mat-icon>check</mat-icon>The lines add up to the quote total
                    } @else {
                      <mat-icon>error_outline</mat-icon>Lines add up to {{ calculatedTotal | currency }}, {{ (linesGap < 0 ? -linesGap : linesGap) | currency }} {{ linesGap < 0 ? 'less' : 'more' }} than the quote total. Proposals use the line prices, so check them.
                    }
                  </span>
                } @else if (documentCheckTotal == null) {
                  <span class="match no"><mat-icon>error_outline</mat-icon>Document total not found. Check the lines against the document.</span>
                } @else {
                  <span class="match" [class.no]="!totalMatches">
                    <mat-icon>{{ totalMatches ? 'check' : 'error_outline' }}</mat-icon>
                    @if (totalMatches) {
                      Matches the document total of {{ documentCheckTotal | currency }}
                    } @else {
                      Document says {{ documentCheckTotal | currency }}, {{ documentDifference | currency }} difference. Check the lines above.
                    }
                  </span>
                }
              </div>
            </section>
          </div>
        </div>
      }
</ng-template>

<!-- D9: ✦ / ✎ source badge next to a label -->
<ng-template #srcBadge let-field="field" let-label="label">
  @if (field.source === 'manual') {
    <span class="srcb man" tabindex="0" [matTooltip]="sourceTooltip(field)" [attr.aria-label]="label + ': entered manually'">✎</span>
  } @else if (field.source === 'calculated') {
    <span class="srcb calc" tabindex="0" [matTooltip]="sourceTooltip(field)" [attr.aria-label]="label + ': calculated, not from the document'"><mat-icon>calculate</mat-icon></span>
  } @else {
    <span class="srcb" tabindex="0" [matTooltip]="sourceTooltip(field)" [attr.aria-label]="label + ': read from the document'"><mat-icon>auto_awesome</mat-icon></span>
  }
</ng-template>

<ng-template #readOnlyField let-label="label" let-field="field">
  <div class="f">
    <span class="k">{{ label }}<ng-container *ngTemplateOutlet="srcBadge; context: { field: field, label: label }"></ng-container></span>
    <span class="v">{{ field.value }}</span>
  </div>
</ng-template>

<!-- D7: NEW PRODUCT tooltip -->
@if (newProductTip; as tip) {
  <div class="vqr-ntip" role="tooltip" [style.left.px]="tip.x" [style.top.px]="tip.y">
    <div class="h"><b>Not in your product catalog</b></div>
    <p>We searched your products by code <b>{{ tip.line.code }}</b> and name <b>{{ searchedName(tip.line) }}</b> and found no match.</p>
    <p>When you <b>save the quote</b>, ProSuite adds it to your catalog as a new product:</p>
    <dl>
      <dt>Name</dt><dd>{{ tip.line.name }}</dd>
      <dt>Description</dt><dd>{{ tip.line.description }}</dd>
      <dt>Unit</dt><dd>{{ tip.line.unit }}</dd>
      <dt>Vendor price</dt><dd>{{ tip.line.unitPrice.value | currency }}</dd>
      <dt>Vendor</dt><dd>{{ draft?.vendor?.name?.value }}</dd>
    </dl>
    <p class="f">The quote line is then linked to it, and it can be used in proposals like any other product.</p>
  </div>
}
